import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { emptyFusionHubApi } from "./emptyFusionHubApi.ts";
import type {
	PaginatedParams,
	FusionhubIntegration,
	PaginatedResponse,
	ErLegalEntity,
	ErDocumentation,
	FusionHubJob,
	FusionHubJobStatus,
	IntegrationIssue,
} from "./types.ts";
import type { SourceSystemName } from "./types.ts";

// /integrations/extended caps `take` at 1000 server-side — this bounds how many
// pages the fleet-wide fetch will walk before giving up, well above the current
// fleet size (~2300 active integrations as of 2026-08).
const MAX_FLEET_WIDE_PAGES = 25;

export type LocalizedString = {
	en: string;
	sv: string;
};

// Mirrors FusionHub's FieldDefinitionSchema (GET /integration-sources).
// Display strings arrive localized — rendered directly, never through t().
export type FieldDefinition = {
	key: string;
	label: LocalizedString;
	description?: LocalizedString;
	placeholder?: LocalizedString;
	required?: boolean;
	requiredOnCreate?: boolean;
	format?: "password";
	// Wire type for this field's value. HTML inputs only ever produce strings,
	// so DynamicFieldFormModal converts to this type before POSTing. Defaults
	// to "string" — FusionHub enforces this matches the create body schema.
	valueType?: "string" | "number";
};

export type ResourceSelectDescriptor = {
	mode: "single" | "multi";
	label: LocalizedString;
};

export type Resource = {
	id: string;
	name: string;
	active: boolean;
};

export type IntegrationSource = {
	name: SourceSystemName;
	displayName: string;
	tags: string[];
	logoUrl: string;
	hasErAdapter: boolean;
	erResources: string[];
	selfService: boolean;
	underDevelopment: boolean;
	supportsInvoiceImages: boolean;
	fields?: FieldDefinition[];
	resourceSelect?: ResourceSelectDescriptor;
};

export type FusionHubExtendedIntegration = {
	integration: FusionhubIntegration;
	lastSuccessfulSyncAt: string | null;
	currentStatus:
		| "waiting"
		| "running"
		| "completed"
		| "failed"
		| "paused"
		| null;
	name: string;
	organisationNumber: string | null;
	// Active (unresolved) issues only. Optional because the portal deploys
	// independently of FusionHub — a backend without the issues feature omits it.
	issues?: IntegrationIssue[] | undefined;
};

// Mirrors GET /integration-statuses (FusionHub#3596) — DB-native fields only,
// no per-row vendor call, so no `name`/`organisationNumber` (unlike
// FusionHubExtendedIntegration above). Flat, not `{ integration, ... }`
// nested — this is its own resource, not a variant of `/integrations/extended`.
export type FusionHubIntegrationStatus = {
	id: number;
	type: SourceSystemName;
	companyDomainId: string | null;
	isPaused: boolean;
	pausedAt: string | null;
	pausedBy: string | null;
	pausedReason: string | null;
	currentStatus:
		| "waiting"
		| "running"
		| "completed"
		| "failed"
		| "paused"
		| null;
	lastSuccessfulSyncAt: string | null;
	issues?: IntegrationIssue[] | undefined;
};

export const fusionHubApi = emptyFusionHubApi.injectEndpoints({
	endpoints: (builder) => ({
		getErAdapters: builder.query<SourceSystemName[], void>({
			query: () => "/er/adapters",
		}),
		getIntegrationSources: builder.query<IntegrationSource[], void>({
			query: () => "/integration-sources",
			keepUnusedDataFor: 3600,
		}),
		getErDocumentation: builder.query<ErDocumentation, void>({
			query: () => "/er/documentation",
		}),
		getAllIntegrations: builder.query<
			PaginatedResponse<FusionHubExtendedIntegration>,
			{ companyDomainId: string }
		>({
			query: ({ companyDomainId }) => ({
				url: "/integrations/extended",
				params: {
					take: 1000,
					companyDomainId,
				},
			}),
			providesTags: (result) => [
				{
					type: "Integrations",
				},
				...(result?.items ?? []).map((item) => {
					return { type: "Integrations" as const, id: item.integration.id };
				}),
			],
		}),
		// Fleet-wide: no companyDomainId, superadmin-gated. Walks every page since the
		// admin status page filters/sorts client-side across the whole fleet.
		getAllIntegrationsExtended: builder.query<
			{ items: FusionHubExtendedIntegration[]; isTruncated: boolean },
			void
		>({
			async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
				const items: FusionHubExtendedIntegration[] = [];
				let cursor: string | undefined;
				let isTruncated = false;

				for (let page = 0; page < MAX_FLEET_WIDE_PAGES; page++) {
					const result = await baseQuery({
						url: "/integrations/extended",
						params: { take: 1000, ...(cursor ? { cursor } : {}) },
					});

					if (result.error) {
						return { error: result.error as FetchBaseQueryError };
					}

					const paginated =
						result.data as PaginatedResponse<FusionHubExtendedIntegration>;
					items.push(...paginated.items);

					if (!paginated.page.cursor) {
						break;
					}
					cursor = paginated.page.cursor;
					// Ran out of page budget with more data still on the server —
					// surface this rather than silently showing a partial fleet.
					isTruncated = page === MAX_FLEET_WIDE_PAGES - 1;
				}

				return { data: { items, isTruncated } };
			},
			providesTags: (result) => [
				{ type: "Integrations" },
				...(result?.items ?? []).map((item) => ({
					type: "Integrations" as const,
					id: item.integration.id,
				})),
			],
		}),

		// Fleet-wide, cheap: DB-native fields only, no vendor calls (FusionHub#3596).
		// Walks every page for the same reason getAllIntegrationsExtended does —
		// the fleet page filters/sorts client-side across the whole result.
		getAllIntegrationStatuses: builder.query<
			{ items: FusionHubIntegrationStatus[]; isTruncated: boolean },
			void
		>({
			async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
				const items: FusionHubIntegrationStatus[] = [];
				let cursor: string | undefined;
				let isTruncated = false;

				for (let page = 0; page < MAX_FLEET_WIDE_PAGES; page++) {
					const result = await baseQuery({
						url: "/integration-statuses",
						params: { take: 1000, ...(cursor ? { cursor } : {}) },
					});

					if (result.error) {
						return { error: result.error as FetchBaseQueryError };
					}

					const paginated =
						result.data as PaginatedResponse<FusionHubIntegrationStatus>;
					items.push(...paginated.items);

					if (!paginated.page.cursor) {
						break;
					}
					cursor = paginated.page.cursor;
					isTruncated = page === MAX_FLEET_WIDE_PAGES - 1;
				}

				return { data: { items, isTruncated } };
			},
			providesTags: (result) => [
				{ type: "Integrations" },
				...(result?.items ?? []).map((item) => ({
					type: "Integrations" as const,
					id: item.id,
				})),
			],
		}),

		notifyIntegrationIssues: builder.mutation<void, { issueIds: number[] }>({
			query: ({ issueIds }) => ({
				url: "/integrations/issues/notify",
				method: "POST",
				body: { issueIds },
			}),
			invalidatesTags: [{ type: "Integrations" }],
		}),

		getErIntegrations: builder.query<
			PaginatedResponse<FusionhubIntegration>,
			{ companyDomainId: string } & PaginatedParams
		>({
			query: (params) => ({
				url: "/er/integrations",
				params,
			}),
			providesTags: (result) => [
				{
					type: "Integrations",
				},
				...(result?.items ?? []).map((item) => {
					return { type: "Integrations" as const, id: item.id };
				}),
			],
		}),

		getErLegalEntity: builder.query<ErLegalEntity, { integrationId: number }>({
			query: ({ integrationId }) =>
				`/er/integrations/${integrationId}/legal-entity`,
		}),

		getErLegalEntities: builder.query<
			PaginatedResponse<ErLegalEntity>,
			{ integrationId: number } & PaginatedParams
		>({
			query: ({ integrationId, ...params }) => ({
				url: `/er/integrations/${integrationId}/legal-entities`,
				params,
			}),
			providesTags: (_result, _error, { integrationId }) => [
				{ type: "Integrations", id: integrationId },
			],
		}),

		fetchResources: builder.mutation<
			Resource[],
			{
				sourceName: SourceSystemName;
				values: Record<string, string | number>;
			}
		>({
			query: ({ sourceName, values }) => ({
				url: `/integration-sources/${sourceName}/resources`,
				method: "POST",
				body: values,
			}),
		}),

		createIntegration: builder.mutation<
			FusionhubIntegration | { integrations: FusionhubIntegration[] },
			{
				type: SourceSystemName;
				companyDomainId: string;
				values: Record<string, string | number>;
				resourceIds?: string[];
			}
		>({
			query: ({ type, companyDomainId, values, resourceIds }) => ({
				url: "/integrations",
				method: "POST",
				body: {
					type,
					companyDomainId,
					values,
					...(resourceIds && { resourceIds }),
				},
			}),
			invalidatesTags: [{ type: "Integrations" }],
		}),

		triggerSync: builder.mutation<void, { integrationIds: number[] }>({
			query: ({ integrationIds }) => ({
				url: "/integrations/sync",
				method: "POST",
				body: { integrationIds },
			}),
			invalidatesTags: (_result, _error, { integrationIds }) => {
				return integrationIds.flatMap((integrationId) => [
					{ type: "SyncStatus", id: integrationId },
					{ type: "Integrations", id: integrationId },
					{ type: "JobInIntegration", id: integrationId },
				]);
			},
		}),

		pauseIntegration: builder.mutation<
			void,
			{ integrationId: number; reason: string }
		>({
			query: ({ integrationId, reason }) => ({
				url: `/integrations/${integrationId}/pause`,
				method: "POST",
				body: { reason },
			}),
			invalidatesTags: (_result, _error, { integrationId }) => {
				return [
					{ type: "SyncStatus", id: integrationId },
					{ type: "Integrations", id: integrationId },
					{ type: "JobInIntegration", id: integrationId },
				];
			},
		}),

		unpauseIntegration: builder.mutation<void, { integrationId: number }>({
			query: ({ integrationId }) => ({
				url: `/integrations/${integrationId}/unpause`,
				method: "POST",
			}),
			invalidatesTags: (_result, _error, { integrationId }) => {
				return [
					{ type: "SyncStatus", id: integrationId },
					{ type: "Integrations", id: integrationId },
					{ type: "JobInIntegration", id: integrationId },
				];
			},
		}),

		resetSyncCursors: builder.mutation<
			void,
			{ integrationId: number; date: string }
		>({
			query: ({ integrationId, date }) => ({
				url: `/integrations/${integrationId}/reset-sync-cursors`,
				method: "POST",
				body: { date },
			}),
			invalidatesTags: (_result, _error, { integrationId }) => {
				return [
					{ type: "SyncStatus", id: integrationId },
					{ type: "Integrations", id: integrationId },
					{ type: "JobInIntegration", id: integrationId },
				];
			},
		}),

		removeIntegration: builder.mutation<void, { integrationId: number }>({
			query: ({ integrationId }) => ({
				url: `/integrations/${integrationId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, { integrationId }) => [
				{ type: "SyncStatus", id: integrationId },
				{ type: "Integrations", id: integrationId },
				{ type: "JobInIntegration", id: integrationId },
			],
		}),

		getJobs: builder.query<
			PaginatedResponse<FusionHubJob>,
			{
				companyDomainId?: string | undefined;
				integrationId?: number | undefined;
				status?: FusionHubJobStatus[] | undefined;
				jobNames?: string[] | undefined;
			} & PaginatedParams
		>({
			query({ status, jobNames, ...params }) {
				const searchParams = new URLSearchParams();
				if (status) {
					for (const s of status) {
						searchParams.append("status", s);
					}
				}

				if (jobNames) {
					for (const j of jobNames) {
						searchParams.append("jobNames", j);
					}
				}
				return {
					url: `/jobs?${searchParams.toString()}`,
					params: params,
				};
			},
			providesTags(result, _error, args) {
				return [
					args.integrationId
						? { type: "JobInIntegration" as const, id: args.integrationId }
						: null,
					...(result
						? result.items.map((job) => ({ type: "Job" as const, id: job.id }))
						: [{ type: "Job" as const }]),
				];
			},
		}),

		getJob: builder.query<FusionHubJob, { jobId: string }>({
			query(params) {
				return {
					url: `/jobs/${params.jobId}`,
					params,
				};
			},
			providesTags(result, _error, args) {
				return [
					{ type: "Job" as const, id: `${args.jobId}` },
					...(result && result.integrationIds !== null
						? result.integrationIds.map((id) => ({
								type: "JobInIntegration" as const,
								id,
							}))
						: []),
				];
			},
		}),

		getIntegration: builder.query<
			FusionhubIntegration,
			{ integrationId: number }
		>({
			query({ integrationId }) {
				return {
					url: `/integrations/${integrationId}`,
				};
			},
			providesTags: (_result, _error, { integrationId }) => [
				{ type: "Integrations", id: integrationId },
			],
		}),

		getOrganisationInfo: builder.query<
			OrganisationInfo,
			{ organisationNumber: string }
		>({
			query: ({ organisationNumber }) =>
				`/organisation-info/${organisationNumber}`,
		}),

		getAnnualReports: builder.query<
			AnnualReportsResponse,
			{ organisationNumber: string }
		>({
			query: ({ organisationNumber }) =>
				`/organisation-info/${organisationNumber}/annual-reports`,
		}),
	}),
	overrideExisting: "throw",
});

export type OrganisationInfo = {
	organisationsidentitet: {
		identitetsbeteckning: string;
		typ: { kod: string; klartext: string } | null;
	};
	organisationsnamn: {
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
		organisationsnamnLista: Array<{
			namn: string;
			organisationsnamntyp: { kod: string; klartext: string } | null;
			registreringsdatum: string | null;
			verksamhetsbeskrivningSarskiltForetagsnamn?: string | null;
		}>;
	};
	namnskyddslopnummer: number | null;
	registreringsland: { kod: string; klartext: string } | null;
	organisationsform: {
		kod: string;
		klartext: string;
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
	} | null;
	juridiskForm: {
		kod: string;
		klartext: string;
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
	} | null;
	verksamOrganisation: {
		kod: string;
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
	} | null;
	organisationsdatum: {
		registreringsdatum: string | null;
		infortHosScb?: string | null;
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
	} | null;
	verksamhetsbeskrivning: {
		beskrivning: string | null;
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
	} | null;
	naringsgrenOrganisation: {
		sni: Array<{ kod: string; klartext: string }>;
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
	} | null;
	postadressOrganisation: {
		postadress: {
			postnummer: string | null;
			utdelningsadress: string | null;
			land: string | null;
			coAdress: string | null;
			postort: string | null;
		} | null;
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
	} | null;
	reklamsparr: {
		kod: string;
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
	} | null;
	avregistreradOrganisation: {
		avregistreringsdatum: string | null;
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
	} | null;
	avregistreringsorsak: {
		kod: string | null;
		klartext: string | null;
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
	} | null;
	pagaendeAvvecklingsEllerOmstruktureringsforfarande: {
		pagaendeAvvecklingsEllerOmstruktureringsforfarandeLista: Array<{
			kod: string;
			klartext: string;
			fromDatum: string | null;
		}> | null;
		dataproducent: string;
		fel: { typ: string; felBeskrivning?: string | null } | null;
	} | null;
};

export type AnnualReportsResponse = {
	dokument: Array<{
		dokumentId: string;
		filformat: string;
		rapporteringsperiodTom: string;
		registreringstidpunkt: string;
	}>;
};

export const {
	useGetAllIntegrationsQuery,
	useGetAllIntegrationsExtendedQuery,
	useGetAllIntegrationStatusesQuery,
	useNotifyIntegrationIssuesMutation,
	useGetErAdaptersQuery,
	useGetIntegrationSourcesQuery,
	useGetErIntegrationsQuery,
	useGetErLegalEntityQuery,
	useGetErLegalEntitiesQuery,
	useGetErDocumentationQuery,
	useFetchResourcesMutation,
	useCreateIntegrationMutation,
	useTriggerSyncMutation,
	useRemoveIntegrationMutation,
	useGetJobsQuery,
	useGetJobQuery,
	useGetIntegrationQuery,
	usePauseIntegrationMutation,
	useUnpauseIntegrationMutation,
	useResetSyncCursorsMutation,
	useGetOrganisationInfoQuery,
	useGetAnnualReportsQuery,
} = fusionHubApi;
